(ns io.daskul.webapp.routes
  "The aggregate route fragment, shared by every product's router.

   Plain data — no reitit dependency. Each product splices `aggregate-routes`
   into its own route tree under `/:school-code` and builds the router itself.

   It lives here rather than being copied into each product because the ORDER
   of these routes is load-bearing (see below) and a duplicated invariant is an
   invariant that drifts. `routes-test` pins the behaviour; `daskul-basiced`
   has no cljs test harness of its own, so sharing the data is also what gets
   it covered.")

(def aggregate-routes
  "Aggregate CRUD routes. **Order is significant — do not sort or regroup.**

   `/a/:agg/:id` conflicts with both `/a/:agg/new` and `/a/:agg/edit/:id`, and
   reitit resolves such overlaps by DECLARATION ORDER, not by preferring the
   more literal segment. Probed on reitit-frontend 0.9.1: declaring `:id`
   first makes `/a/leads/new` match `:aggregate/detail` with `:id \"new\"`, so
   creating a record silently breaks in both products.

   Two consequences:

   1. The router must be built with `{:conflicts nil}`. reitit does not
      mis-route these — it refuses to build the router at all, which is a
      startup crash, so the opt-out is required rather than merely advisable.
   2. Literal-prefixed routes MUST precede the `:id` wildcards. `routes-test`
      asserts the resolution this ordering produces.

   Route shapes (F-060 — the detail page absorbs editing):

     /a/:agg           list
     /a/:agg/new       create
     /a/:agg/edit/:id  LEGACY — redirects to /a/:agg/:id, see below
     /a/:agg/:id       canonical record URL, Details tab
     /a/:agg/:id/:tab  tab as a path segment, deep-linkable

   `:aggregate/edit` is retired from the UI but kept in the router as
   `:aggregate/edit-legacy`, which redirects. Those URLs are pasted into
   Messenger threads and bookmarked by admins, and the conversation sidebar
   deep-links that exact shape — deleting the route would break every one of
   them at once.

   `:tab` and any future read-only view share ONE namespace: a view segment
   cannot be named the same as a tab on the same aggregate."
  [["/a/:agg"          {:name :aggregate/list}]
   ["/a/:agg/new"      {:name :aggregate/new}]
   ["/a/:agg/edit/:id" {:name :aggregate/edit-legacy}]
   ["/a/:agg/:id"      {:name :aggregate/detail}]
   ["/a/:agg/:id/:tab" {:name :aggregate/detail-tab}]])

(def router-opts
  "Options every product router must pass. See `aggregate-routes`."
  {:conflicts nil})

(def detail-route-names
  "Routes that render a single record. Used to decide whether a match is a
   record page without enumerating names at each call site."
  #{:aggregate/detail :aggregate/detail-tab})

(defn legacy-edit-match?
  "True when a route match is the retired `/a/:agg/edit/:id` shape and should
   be redirected to the canonical record URL."
  [match]
  (= :aggregate/edit-legacy (get-in match [:data :name])))

;; ---------------------------------------------------------------------------
;; "Is there anywhere to go back to?"
;;
;; `js/window.history.back` is the right behaviour for the in-page Back link —
;; it returns to the previous TAB (tabs are real history entries since they
;; became path segments) or to the list with its filters and scroll intact.
;; It has one hole: arrive by deep link — a Messenger link, a bookmark, a
;; pasted URL — and there is nothing behind it, so Back leaves the app.
;;
;; Neither browser signal detects this:
;;   history.length    counts the whole TAB session across every origin
;;                     visited, so it is >1 even on a fresh deep link
;;   document.referrer empty for direct navigation, and empty in practice for
;;                     plenty of in-app cases too
;;
;; What does work is counting our own navigations: reitit calls on-navigate
;; once for the initial match and once per navigation after it, so a count of
;; 1 means "landed here, never moved".
;; ---------------------------------------------------------------------------

(defonce ^:private navigation-count (atom 0))

(defn note-navigation!
  "Call from the router's on-navigate, once per match."
  []
  (swap! navigation-count inc))

(defn can-go-back?
  "True when at least one in-app navigation has happened since load, so
   `history.back` lands somewhere inside the app."
  []
  (> @navigation-count 1))

(defn back-fallback-target
  "Where Back should go when there is no in-app history — pure, so it is
   testable without a router. Record pages fall back to their own list;
   anything else falls back to home.

   Returns `[route-name path-params]`."
  [match]
  (let [route-name (get-in match [:data :name])
        {:keys [school-code agg]} (:path-params match)]
    (if (contains? detail-route-names route-name)
      [:aggregate/list (cond-> {:agg agg}
                         school-code (assoc :school-code school-code))]
      [:home (cond-> {} school-code (assoc :school-code school-code))])))

(defn legacy-edit-redirect
  "The canonical target for a legacy `/a/:agg/edit/:id` match, as
   `[route-name path-params query-params]`.

   A `?tab=` query param was the old way of addressing a tab; it becomes a
   path segment, so an old deep link into a specific tab still lands on that
   tab rather than silently dropping to Details.

   Products apply this with `replace-state`, never `push-state` — a redirect
   that pushes leaves the retired URL in history, so Back bounces off it."
  [match]
  (let [path-params (:path-params match)
        query-params (:query-params match)
        tab (some-> (:tab query-params) name not-empty)]
    (if tab
      [:aggregate/detail-tab (assoc path-params :tab tab) (dissoc query-params :tab)]
      [:aggregate/detail path-params query-params])))
